import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  type KeyboardEvent,
} from 'react';

import { useSetHoveredMarkerId } from './store/use-set-hovered-marker-id.js';
import { ORIENTATION } from '../../../chart-layout/types/constants.js';
import { useOrientation } from '../context/RangeLegendOrientationContext.js';

export const useLegendSegmentKeyboardHandler = (
  rectEl: SVGRectElement | null,
  itemFrom: number,
  itemTo: number,
  startPosition: number,
) => {
  const isFirstSegment = startPosition === 0;
  const hoveredMarkerId = useRef<number>(isFirstSegment ? itemFrom : itemTo);

  const { setHoveredMarkerId } = useSetHoveredMarkerId();
  const { orientation } = useOrientation();

  const moveFocusForward = useCallback(() => {
    rectEl?.parentElement?.nextElementSibling?.querySelector('rect')?.focus();
    rectEl?.blur();
  }, [rectEl]);

  const moveFocusBackward = useCallback(() => {
    rectEl?.parentElement?.previousElementSibling
      ?.querySelector('rect')
      ?.focus();
    rectEl?.blur();
  }, [rectEl]);

  const movePositionForward = useCallback(() => {
    hoveredMarkerId.current = itemTo;
    setHoveredMarkerId(hoveredMarkerId.current);
  }, [itemTo, setHoveredMarkerId]);

  const movePositionBackward = useCallback(() => {
    hoveredMarkerId.current = itemFrom;
    setHoveredMarkerId(hoveredMarkerId.current);
  }, [itemFrom, setHoveredMarkerId]);

  const KEYDOWN_HANDLERS: Record<string, Record<string, () => void>> = useMemo(
    () => ({
      [ORIENTATION.Horizontal]: {
        ArrowRight: () => {
          const isFirstPosition =
            hoveredMarkerId.current === itemFrom && isFirstSegment;

          if (isFirstPosition) {
            movePositionForward();
          } else {
            moveFocusForward();
          }
        },
        ArrowLeft: () => {
          const isSecondPosition =
            hoveredMarkerId.current === itemTo && isFirstSegment;

          if (isSecondPosition) {
            movePositionBackward();
          } else {
            moveFocusBackward();
          }
        },
      },
      [ORIENTATION.Vertical]: {
        ArrowDown: () => {
          const isFirstPosition =
            hoveredMarkerId.current === itemFrom && isFirstSegment;

          if (isFirstPosition) {
            movePositionForward();
          } else {
            moveFocusForward();
          }
        },
        ArrowUp: () => {
          const isSecondPosition =
            hoveredMarkerId.current === itemTo && isFirstSegment;

          if (isSecondPosition) {
            movePositionBackward();
          } else {
            moveFocusBackward();
          }
        },
      },
    }),
    [
      isFirstSegment,
      itemFrom,
      itemTo,
      moveFocusBackward,
      moveFocusForward,
      movePositionBackward,
      movePositionForward,
    ],
  );

  const onFocusHandler = useCallback(
    () => setHoveredMarkerId(hoveredMarkerId.current),
    [setHoveredMarkerId],
  );

  const onBlurHandler = useCallback(
    () => setHoveredMarkerId(null),
    [setHoveredMarkerId],
  );

  const onKeyDownHandler = useCallback(
    (event: KeyboardEvent<SVGRectElement>) => {
      if (event.key === 'Tab') {
        const isFirstPosition =
          hoveredMarkerId.current === itemFrom && isFirstSegment;

        if (isFirstPosition) {
          event.preventDefault();
          hoveredMarkerId.current = itemTo;
          setHoveredMarkerId(hoveredMarkerId.current);
        }
      }

      if (event.key in KEYDOWN_HANDLERS[orientation]) {
        KEYDOWN_HANDLERS[orientation][event.key]();
      }
    },
    [
      KEYDOWN_HANDLERS,
      isFirstSegment,
      itemFrom,
      itemTo,
      orientation,
      setHoveredMarkerId,
    ],
  );

  useLayoutEffect(() => {
    if (isFirstSegment) {
      const parentSvg = rectEl?.closest('svg');

      if (parentSvg) {
        const eventListenerCallback = (event: FocusEvent) => {
          event.preventDefault();
          hoveredMarkerId.current = itemFrom;
        };

        parentSvg.addEventListener('blur', eventListenerCallback);

        return () =>
          parentSvg.removeEventListener('blur', eventListenerCallback);
      }
    }
  }, [isFirstSegment, itemFrom, rectEl]);

  return {
    onBlurHandler,
    onFocusHandler,
    onKeyDownHandler,
  };
};
